/**
 * TanStack Query hooks for API key management.
 *
 * Provides queries and mutations for the API key lifecycle:
 * create, list, and revoke.
 */

import { useApiClient, useApiMutation, useApiQuery } from "@frontend/hooks/use-api"
import { useMutation, useQueryClient } from "@tanstack/react-query"

/** API key as returned from the list endpoint (masked) */
export interface ApiKeyItem {
  id: string
  name: string
  prefix: string
  last_four: string
  expires_at: string | null
  created_at: string
}

/** API key as returned from the create endpoint (includes full key) */
export interface CreatedApiKey extends ApiKeyItem {
  key: string
}

/** List response from GET /api-keys */
interface ApiKeyListResponse {
  data: ApiKeyItem[]
}

/**
 * Fetch the current user's active API keys.
 */
export function useApiKeys() {
  return useApiQuery<ApiKeyListResponse>(["apiKeys"], "/api-keys")
}

/**
 * Create a new API key.
 * Returns the full key in the response (one-time display).
 */
export function useCreateApiKey() {
  return useApiMutation<CreatedApiKey, { name: string; expires_at?: string | null }>("/api-keys", {
    method: "POST",
    invalidateKeys: [["apiKeys"]]
  })
}

/**
 * Revoke an API key by ID.
 * Uses DELETE /api-keys/:keyId with the keyId as the mutation variable.
 * Invalidates the API keys list on success.
 */
export function useRevokeApiKey() {
  const api = useApiClient()
  const queryClient = useQueryClient()

  return useMutation<{ message: string }, Error, string>({
    mutationFn: (keyId: string) => api(`/api-keys/${keyId}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["apiKeys"] })
    }
  })
}
